import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, FileText, Loader2, Download, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, API_URL } from "@/lib/api";
import { PulseLogo } from "@/components/site/Header";
import VinylLoader from "@/components/site/VinylLoader";

export default function ContractSign() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signature, setSignature] = useState("");
  const [signing, setSigning] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showOutro, setShowOutro] = useState(false);
  const sessionId = params.get("session_id");
  const paymentType = params.get("payment_type");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sign/${token}`);
      setContract(data);
    } catch (e) {
      setContract(false);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll status if returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const max = 8;
    setPaymentStatus("checking");
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentStatus("paid");
          toast.success(`${paymentType === "deposit" ? "Deposit" : "Final payment"} received.`);
          await load();
          return;
        }
        if (data.status === "expired") { setPaymentStatus("expired"); return; }
        if (attempts < max) setTimeout(tick, 2000);
        else setPaymentStatus("timeout");
      } catch {
        if (attempts < max) setTimeout(tick, 2000);
        else setPaymentStatus("error");
      }
    };
    tick();
  }, [sessionId, paymentType, load]);

  const sign = async (e) => {
    e.preventDefault();
    setSigning(true);
    try {
      await api.post(`/sign/${token}`, { signature_name: signature });
      toast.success("Signed. Thank you.");
      setShowOutro(true);
      await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Sign failed"); }
    finally { setSigning(false); }
  };

  const pay = async (type) => {
    setPaying(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post(`/payments/checkout`, {
        contract_token: token, payment_type: type, origin_url: origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Payment init failed");
      setPaying(false);
    }
  };

  if (loading) return <div className="pt-40 flex justify-center"><VinylLoader size={80} label="Loading agreement" /></div>;
  if (contract === false) return <div className="pt-40 text-center text-zinc-400" data-testid="contract-not-found">This link is invalid or expired.</div>;

  const total = (contract.services || []).reduce((s, l) => s + (l.rate || 0) * (l.quantity || 1), 0);
  const isSigned = !!contract.signed_at;

  return (
    <div className="min-h-screen pt-32 pb-20 px-6" data-testid="contract-sign-page">
      {/* Outro stinger — plays once after a successful signature */}
      <AnimatePresence>
        {showOutro && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.6 } }}
            data-testid="outro-stinger"
          >
            <video
              autoPlay
              muted
              playsInline
              onEnded={() => setShowOutro(false)}
              onError={() => setShowOutro(false)}
              className="w-full h-full object-cover"
              data-testid="outro-stinger-video"
            >
              <source src="/assets/stinger.mp4" type="video/mp4" />
              <source src="/assets/stinger.mov" type="video/quicktime" />
            </video>
            <div className="absolute bottom-10 left-0 right-0 text-center text-white pointer-events-none">
              <div className="font-display text-3xl md:text-5xl font-bold gradient-text">Signed.</div>
              <div className="label-mono mt-2 text-cyan-300/80">/ Welcome to Pulse Media</div>
            </div>
            <button
              onClick={() => setShowOutro(false)}
              className="absolute bottom-8 right-8 glass-btn ghost text-xs"
              data-testid="outro-skip"
            >
              Continue →
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <PulseLogo size={42} />
          <div>
            <div className="font-display text-xl font-bold tracking-tight">PULSE MEDIA</div>
            <div className="label-mono">/ Service Agreement</div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-white/10 bg-[#0c0c0e] p-8 md:p-12"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="badge">{contract.template.replace("_"," ").toUpperCase()}</div>
            <div className="flex items-center gap-2">
              <a href={`${API_URL}/sign/${token}/pdf`} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-2" title="Download PDF" data-testid="contract-download-pdf"><Download size={14}/></a>
              {isSigned ? <div className="badge success"><CheckCircle2 size={12}/> SIGNED</div> : <div className="badge warn">PENDING SIGNATURE</div>}
            </div>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mb-2">{contract.project_title}</h1>
          <p className="text-zinc-400 mb-8">{contract.project_description}</p>

          <div className="grid md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-white/5">
            <div>
              <div className="label-mono mb-1">Client</div>
              <div className="text-white">{contract.client_name}</div>
              <div className="text-zinc-500 text-sm">{contract.client_email}</div>
              {contract.client_company && <div className="text-zinc-500 text-sm">{contract.client_company}</div>}
            </div>
            <div>
              {contract.event_date && <><div className="label-mono mb-1">Event Date</div><div>{contract.event_date}</div></>}
              {contract.event_location && <><div className="label-mono mt-3 mb-1">Location</div><div>{contract.event_location}</div></>}
            </div>
          </div>

          <div className="mb-8">
            <div className="label-mono mb-3">Services</div>
            <div className="space-y-2">
              {(contract.services || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-white/5">
                  <div>
                    <div className="text-white text-sm">{s.name}</div>
                    <div className="text-zinc-500 text-xs">{s.quantity} × ${s.rate.toFixed(2)} / {s.unit}</div>
                  </div>
                  <div className="font-mono text-white">${(s.rate * s.quantity).toFixed(2)}</div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-4 font-display text-xl">
                <div>Total</div>
                <div>${total.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {contract.terms && (
            <div className="mb-8 p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="label-mono mb-2">Terms Summary</div>
              <p className="text-zinc-400 text-sm whitespace-pre-wrap leading-relaxed">{contract.terms}</p>
            </div>
          )}

          {/* Payment schedule — shown up-front so the client sees deposit vs final before signing */}
          {(contract.deposit_amount > 0 || contract.final_amount > 0) && (
            <div className="mb-8 p-5 rounded-xl bg-cyan-500/[0.04] border border-cyan-400/20">
              <div className="label-mono mb-3 text-cyan-300">Payment Schedule</div>
              <div className="space-y-2">
                {contract.deposit_amount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <div className="text-white font-semibold">Deposit</div>
                      <div className="text-zinc-500 text-xs">{contract.deposit_due_date ? `Due ${contract.deposit_due_date}` : "Confirms your booking"}</div>
                    </div>
                    <div className="font-mono text-white">${contract.deposit_amount.toFixed(2)}</div>
                  </div>
                )}
                {contract.final_amount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <div className="text-white font-semibold">Final Balance</div>
                      <div className="text-zinc-500 text-xs">{contract.final_due_date ? `Due ${contract.final_due_date}` : "Due upon delivery"}</div>
                    </div>
                    <div className="font-mono text-white">${contract.final_amount.toFixed(2)}</div>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-cyan-400/10 text-sm">
                  <div className="text-cyan-300 font-black tracking-widest text-xs">TOTAL</div>
                  <div className="font-mono text-cyan-300 font-black">${((contract.deposit_amount || 0) + (contract.final_amount || 0)).toFixed(2)}</div>
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 mt-3">
                Payment is available via Stripe (card / Apple Pay / Google Pay) on this page immediately after you sign.
              </div>
            </div>
          )}

          {/* Full Service Agreement — expandable so client reviews the legal terms before signing */}
          <FullAgreement contract={contract} />

          {/* Signature */}
          {!isSigned ? (
            <form onSubmit={sign} className="space-y-4" data-testid="contract-sign-form">
              <div>
                <label className="label-mono block mb-2">Type your full name to sign</label>
                <input className="pulse-input" required value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Full legal name" data-testid="contract-signature" />
              </div>
              <button type="submit" disabled={signing} className="glass-btn solid w-full justify-center" data-testid="contract-sign-submit">
                {signing ? "Signing..." : "Sign Agreement"}
              </button>
            </form>
          ) : (
            <div className="p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 mb-6">
              <div className="flex items-center gap-2 text-emerald-400 mb-1"><CheckCircle2 size={16} /> <span className="font-semibold">Signed</span></div>
              <div className="text-sm text-zinc-400">By <b>{contract.signature_name}</b> on {new Date(contract.signed_at).toLocaleString()}</div>
            </div>
          )}

          {/* Payments */}
          {isSigned && (
            <div className="mt-8 pt-8 border-t border-white/5 space-y-4">
              <div className="label-mono">Payments</div>
              {paymentStatus === "checking" && <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Verifying payment...</div>}
              {paymentStatus === "paid" && <div className="text-sm text-emerald-400 flex items-center gap-2"><CheckCircle2 size={14}/> Payment received.</div>}
              {paymentStatus === "expired" && <div className="text-sm text-rose-400">Session expired. Try again.</div>}
              {paymentStatus === "timeout" && <div className="text-sm text-amber-400">Still processing — check back shortly.</div>}

              {contract.deposit_amount > 0 && (
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <div>
                    <div className="text-white">Deposit</div>
                    <div className="text-zinc-500 text-xs">{contract.deposit_due_date ? `Due ${contract.deposit_due_date}` : "Pay now"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-mono">${contract.deposit_amount.toFixed(2)}</div>
                    {contract.deposit_paid
                      ? <div className="badge success">PAID</div>
                      : <button disabled={paying} onClick={() => pay("deposit")} className="glass-btn" data-testid="pay-deposit-btn">Pay Deposit</button>}
                  </div>
                </div>
              )}

              {contract.final_amount > 0 && (
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <div>
                    <div className="text-white">Final Payment</div>
                    <div className="text-zinc-500 text-xs">{contract.final_due_date ? `Due ${contract.final_due_date}` : "Upon completion"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-mono">${contract.final_amount.toFixed(2)}</div>
                    {contract.final_paid
                      ? <div className="badge success">PAID</div>
                      : <button disabled={paying} onClick={() => pay("final")} className="glass-btn" data-testid="pay-final-btn">Pay Final</button>}
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}


/* -------- Full Agreement viewer (accordion of every legal clause) -------- */
function FullAgreement({ contract }) {
  const [open, setOpen] = useState(false);
  const services = (contract.services || []).map((s) => s.name).filter(Boolean);
  const clientName = contract.client_name || "";
  const clientEmail = contract.client_email || "";
  const eventDate = contract.event_date || "TBD";
  const eventLoc = contract.event_location || "TBD";
  const enteredOn = (contract.created_at || "").slice(0, 10);
  const total = ((contract.deposit_amount || 0) + (contract.final_amount || 0)).toFixed(2);
  const deposit = (contract.deposit_amount || 0).toFixed(2);
  const finalBal = (contract.final_amount || 0).toFixed(2);
  const serviceType = ({
    video_shoot: "Music Video Production",
    film_production: "Film Production",
    studio_session: "Studio Recording and Mixing",
  })[contract.template] || "Media Production";

  return (
    <div className="mb-8 rounded-xl bg-white/[0.02] border border-white/5 overflow-hidden" data-testid="full-agreement">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 hover:bg-white/[0.02] transition"
        data-testid="full-agreement-toggle"
      >
        <div className="flex items-center gap-3">
          <FileText size={16} className="text-cyan-400"/>
          <div className="text-left">
            <div className="text-white font-semibold text-sm">Full Service Agreement</div>
            <div className="text-zinc-500 text-xs">Read the legal terms in full before signing</div>
          </div>
        </div>
        <ChevronDown size={16} className={`text-zinc-400 transition ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="p-6 border-t border-white/5 space-y-5 text-sm text-zinc-300 leading-relaxed">
          <div>
            <div className="text-cyan-300 text-xs font-black tracking-widest mb-1">ENTERED INTO ON {enteredOn}</div>
            <div className="text-zinc-500 text-xs">Project is on or around {eventDate} at {eventLoc}.</div>
          </div>
          <Clause title="PARTIES">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-zinc-500 mb-1">Known as &quot;Contractor&quot;</div>
                <div className="text-white font-semibold">Pulse Media LLC</div>
                <div className="text-zinc-400 text-xs">contact@pulsemedia.vip</div>
                <div className="text-zinc-400 text-xs">(216) 952-9733</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 mb-1">Known as &quot;Client&quot;</div>
                <div className="text-white font-semibold">{clientName || "—"}</div>
                <div className="text-zinc-400 text-xs">{clientEmail}</div>
              </div>
            </div>
            <div className="text-xs text-zinc-500 mt-3">Collectively referred to as the &quot;Parties.&quot;</div>
          </Clause>
          <Clause title="PURPOSE OF THE AGREEMENT">
            Client wishes to hire Contractor to provide services relating to Client&apos;s {serviceType} as
            detailed in this Agreement. Contractor has agreed to provide such services according to the terms
            of this Agreement.
          </Clause>
          <Clause title="SERVICES">
            Contractor shall provide Client with the following services (herein known as &quot;Services&quot;):
            <ul className="list-disc pl-5 mt-2 space-y-1">
              {(services.length ? services : [serviceType]).map((s, i) => <li key={i}>{s}</li>)}
            </ul>
            {contract.project_description && (
              <div className="mt-3 text-zinc-400"><b>Project-specific scope:</b> {contract.project_description}</div>
            )}
          </Clause>
          <Clause title="PRE-PROJECT CONSULTATION">
            Contractor shall provide Client with one sixty-minute pre-Service consultation. The Parties shall
            agree on an acceptable date and time for conducting the pre-Service consultation, but the manner by
            which the consultation shall be conducted shall be at the discretion of the Contractor. In most
            instances, this consultation is conducted via phone, video chat, or in person.
          </Clause>
          <Clause title="COST">
            The total cost of all Services Contractor agrees to provide to Client is <b>${total}</b>. Total Cost
            is inclusive of Contractor&apos;s Services, any setup time, travel time and out-of-pocket costs,
            software licenses, administrative fees, assistance, and subcontractor costs. Payment will be broken
            into two parts:
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><b>${deposit}</b> — Deposit due {contract.deposit_due_date || enteredOn} (secures booking)</li>
              <li><b>${finalBal}</b> — Final balance due {contract.final_due_date || eventDate} (upon delivery)</li>
            </ul>
          </Clause>
          <Clause title="EXPENSES">
            Any expenses incurred by Contractor while providing Client with Services will be invoiced to Client
            in a timely manner. Client is responsible for paying for and delivering any third-party software
            licenses or products Client wishes Contractor to utilize; at the Contractor&apos;s discretion, Contractor
            will make reasonable efforts to integrate Client&apos;s suggested software or products.
          </Clause>
          <Clause title="CONFIDENTIALITY">
            <p>Parties will treat and hold all information of or relating to this Agreement, the Services provided,
            and the Parties&apos; businesses in strict confidence and will not use any of this information except in
            connection with fulfilling the terms of this Agreement, and, if this Agreement is terminated for
            whatever reason, Parties will return all such information, including account access information, and
            any and all copies to the original Party and will remain bound to the Confidentiality provision of
            this Agreement.</p>
            <p className="mt-2">Confidential information (herein &quot;Confidential Information&quot;) means information
            that is of value to its owner and is treated as proprietary or confidential including, but not
            limited to, intellectual property, inventions, trade secrets or information, financial data or
            information, speculation, knowledge, general Company data or reports, future business plans,
            strategies, customer lists and information, client acquisition strategies, advertising campaigns,
            information regarding executives and employees, and the terms and provisions of this Agreement.</p>
            <p className="mt-2">Further, at all times neither Party shall use or disclose any Confidential Information
            relating in any way to the past, present, or future business affairs, conditions, clients, customers,
            efforts, employees, financial data, operations, practices, products, processes, properties, sales, or
            services of or relating in any way to the Company in whatever form to any parties outside of this
            Agreement.</p>
            <p className="mt-2">This Agreement imposes no obligation upon the Parties with respect to any Confidential
            Information that was possessed before initial business interactions commenced between the Parties;
            is or becomes a matter of public knowledge through no fault of the receiving Party; is rightfully
            received from a third party not owing a duty of confidentiality; is disclosed without a duty of
            confidentiality to a third party by, or with the authorization of, the disclosing Party; or is
            independently developed by either Party without prior knowledge of privileged or confidential
            information.</p>
          </Clause>
          <Clause title="RELATIONSHIPS OF THE PARTIES">
            Contractor and any related sub-contractors are not employees, partners or members of Client&apos;s
            company or organization. Contractor has the sole right to control and direct the means, manner and
            method by which the services in this Agreement are performed. Contractor has the right to hire
            assistants, subcontractors or employees to provide Client with its Services. Parties are individually
            and separately responsible for their own business operation and expenses, including securing or
            paying any licensing fees, insurance, taxes (including FICA), registrations or permits. Client is
            not responsible for paying for any benefits, Workers Compensation, insurance or unemployment fees to
            Contractor.
          </Clause>
          <Clause title="INTELLECTUAL PROPERTY">
            Any and all work created as a result of Contractor&apos;s Services is considered a work for hire and is
            expressly assigned to and owned by Client upon full payment. Parties own their respective trademarks
            and intellectual property used in the normal and separate course of their business and agree not to
            infringe upon or otherwise use each other&apos;s respective intellectual property except for in the course
            of providing Client with its Services.
          </Clause>
          <Clause title="ASSUMPTION OF RISK">
            Client and related parties / participants expressly assume any risk related to the activities as
            described herein.
          </Clause>
          <Clause title="CANCELLATIONS AND RESCHEDULING">
            <p className="text-white font-semibold text-xs tracking-widest">CONTRACTOR DESIRES TO CANCEL OR RESCHEDULE</p>
            <p className="mt-1">In the event Contractor cannot or will not perform its obligations in any or all
            parts of this Agreement, it (or a responsible party) will immediately give Notice to Client, and at
            the Contractor&apos;s discretion, either attempt to find a reasonable substitute to fulfill the terms of
            this Agreement or issue a refund or credit based on a reasonably accurate percentage of Services
            rendered. In the case of a refund where, at the discretion of the Contractor, no reasonable
            substitute is found, Contractor shall excuse Client of further performance obligations in this
            Agreement.</p>
            <p className="text-white font-semibold text-xs tracking-widest mt-3">NO-SHOWS</p>
            <p className="mt-1">If it becomes impossible for Contractor to render Services due to the fault of the
            Client or parties related to Client, such as failure to provide necessary elements of the Services
            or failure of one or more essential parties to the Services to complete tasks in a timely manner, it
            is within the Contractor&apos;s sole discretion to allow for any additional time or dates to render
            Services. In such an event, any outstanding amount will immediately become due and payable to
            Contractor.</p>
          </Clause>
          <Clause title="NOTICE">
            Parties shall provide effective notice (&quot;Notice&quot;) to each other, including any payments or
            invoices, via either of the following methods of delivery at the date and time which the Notice is
            sent:
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Contractor Email: contact@pulsemedia.vip</li>
              <li>Client Email: {clientEmail}</li>
            </ul>
          </Clause>
          <div className="text-xs text-zinc-500 pt-3 border-t border-white/5">
            The identical text is included in the downloadable PDF above. By typing your name and signing below,
            you agree to the full Agreement as stated.
          </div>
        </div>
      )}
    </div>
  );
}

function Clause({ title, children }) {
  return (
    <div>
      <div className="text-cyan-300 text-xs font-black tracking-widest mb-2">{title}</div>
      <div className="text-zinc-300 text-sm leading-relaxed">{children}</div>
    </div>
  );
}
